import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  ClientRepresentation,
  GroupRepresentation,
  KeycloakAdminApi,
  RoleRepresentation,
  ServiceAccountRoleGroup,
  UserRepresentation,
} from '../../services/keycloak-admin-api';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { environment } from '../../environments/environment';
import { OAuthService } from 'angular-oauth2-oidc';
import { COUNTRY_CODES } from '../../constants/iso-country-codes';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { ToggleableOption } from '../toggleable-option/toggleable-option';
import { DescriptiveSubHeader } from '../descriptive-sub-header/descriptive-sub-header';
import { UserMe } from '../../auth/user-me';
import { DisablePointerEventsDirective } from '../utils/disable-pointer-directive';

interface CustomAttribute {
  key: string;
  value: string;
}

// Identifies the self-audience protocol mapper so it can be toggled idempotently
const AUDIENCE_MAPPER_NAME = 'audience-self';

@Component({
  selector: 'app-client-attributes-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    RichTextEditor,
    ToggleableOption,
    DescriptiveSubHeader,
    DisablePointerEventsDirective,
  ],
  templateUrl: './client-attributes-form.html',
  styleUrl: './client-attributes-form.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ClientAttributesForm implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private oauthService = inject(OAuthService);
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private intlDisplayNames = new Intl.DisplayNames([navigator.language], { type: 'region' });

  clientId: string | null = null;
  clientForm: FormGroup;
  roleForm: FormGroup;
  isLoading = false;
  isSaving = false;
  client: ClientRepresentation | null = null;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  activeTab = 'general';

  // Roles management
  clientRoles: RoleRepresentation[] = [];
  clientPermissions: { user: string; permission: string }[] = [];
  isLoadingRoles = false;
  isLoadingPermissions = false;
  isSavingRole = false;
  showCreateRoleModal = false;
  showEditRoleModal = false;
  showDeleteRoleModal = false;
  selectedRole: RoleRepresentation | null = null;

  // Composite role membership management
  protected showManageCompositesModal = signal(false);
  protected compositeRoleTarget = signal<RoleRepresentation | null>(null);
  protected compositeMembers = signal<RoleRepresentation[]>([]);
  protected isLoadingComposites = signal(false);
  protected isSavingComposite = signal(false);
  protected compositeClientSearchResults = signal<ClientRepresentation[]>([]);
  protected compositeClientResultsTruncated = signal(false);
  protected selectedCompositeClient = signal<ClientRepresentation | 'REALM' | null>(null);
  protected selectedCompositeClientValue = computed(() => {
    const selected = this.selectedCompositeClient();
    return selected === 'REALM' ? 'REALM' : selected?.id || '';
  });
  protected compositeCandidateRoles = signal<RoleRepresentation[]>([]);
  protected selectedCompositeRoleToAdd = signal<RoleRepresentation | null>(null);
  private compositeClientSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Service account role assignment (client credentials flow)
  protected serviceAccountScope = signal<ClientRepresentation | 'REALM' | null>(null);
  protected serviceAccountScopeValue = computed(() => {
    const scope = this.serviceAccountScope();
    return scope === 'REALM' ? 'REALM' : scope?.id || '';
  });
  protected serviceAccountClientSearchResults = signal<ClientRepresentation[]>([]);
  protected serviceAccountClientResultsTruncated = signal(false);
  protected serviceAccountAssignedRoles = signal<RoleRepresentation[]>([]);
  protected serviceAccountAvailableRoles = signal<RoleRepresentation[]>([]);
  protected serviceAccountEffectiveRoles = signal<RoleRepresentation[]>([]);
  protected isLoadingServiceAccountRoles = signal(false);
  protected isSavingServiceAccountRole = signal(false);
  protected selectedServiceAccountRoleToAdd = signal<RoleRepresentation | null>(null);
  protected serviceAccountAllRoleGroups = signal<ServiceAccountRoleGroup[]>([]);
  protected isLoadingServiceAccountAllRoles = signal(false);
  private serviceAccountClientSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private serviceAccountTabInitialized = false;

  // Assign one of this client's own roles to another client's service account
  protected otherServiceAccountClientResults = signal<ClientRepresentation[]>([]);
  protected otherServiceAccountClientResultsTruncated = signal(false);
  protected selectedOtherServiceAccountClient = signal<ClientRepresentation | null>(null);
  protected selectedRoleToGrant = signal<RoleRepresentation | null>(null);
  protected roleGrantIsAssigned = signal(false);
  protected isCheckingRoleGrantStatus = signal(false);
  protected isSavingRoleGrant = signal(false);
  private otherServiceAccountClientSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private otherServiceAccountClientsTabInitialized = false;
  private static readonly OTHER_SERVICE_ACCOUNT_CLIENT_PAGE_SIZE = 20;

  // Admins management
  protected showPermissionModal = signal<boolean>(false);
  protected userSearchResults = signal<UserRepresentation[]>([]);
  protected currentUserId = signal<string>('');
  protected selectedUserEmail = signal<string | null>(null);
  protected selectedPermission = signal<string | null>(null);
  // the common cases, shown above the divider
  iaPrimaryPermissions = ['ia_full_admin', 'ia_users_admin'];
  // niche permissions, shown below the divider
  iaSecondaryPermissions = [
    'ia_basics_admin',
    'ia_salesforce_admin',
    'ia_terms_admin',
    'ia_urls_admin',
    'ia_denyconfig_admin',
  ];
  // human-readable title + description for each FGA permission, keyed by relation name
  protected readonly permissionDetails: Record<string, { title: string; description: string }> = {
    ia_full_admin: {
      title: 'Full Admin',
      description: 'Full control over this client.',
    },
    ia_users_admin: {
      title: 'Users & Roles',
      description: 'Assign roles to users and groups.',
    },
    ia_basics_admin: {
      title: 'Basic Settings',
      description: 'General, authentication and attributes.',
    },
    ia_salesforce_admin: {
      title: 'Salesforce Integration',
      description: 'Salesforce campaign ID config.',
    },
    ia_terms_admin: {
      title: 'Terms & Conditions',
      description: 'Terms settings and messages.',
    },
    ia_urls_admin: {
      title: 'URLs',
      description: 'Root, admin and redirect URLs.',
    },
    ia_denyconfig_admin: {
      title: 'Deny Access',
      description: 'Deny access rules and messages.',
    },
  };

  // Password visibility toggle
  protected showClientSecret = signal(false);

  // Localization management
  protected availableLocales = signal<string[]>([]);
  protected termsMessages = signal<Record<string, string>>({});
  protected denyMessages = signal<Record<string, string>>({});
  private originalTermsMessages = signal<Record<string, string>>({});
  private originalDenyMessages = signal<Record<string, string>>({});
  protected termsEnabled = signal(false);
  protected denyMessageEnabled = signal(false);
  protected isLoadingTermsMessages = signal(false);
  protected isLoadingDenyMessages = signal(false);
  protected isSavingLocalization = signal(false);
  protected termsLocalizationKey = signal('');
  protected denyLocalizationKey = signal('');
  protected showDeleteTermsConfirm = signal(false);
  protected showDeleteDenyConfirm = signal(false);

  protected termsLocaleEntries = computed(() => Object.entries(this.termsMessages()));
  protected denyLocaleEntries = computed(() => Object.entries(this.denyMessages()));

  protected unusedTermsLocales = computed(() => {
    const used = new Set(Object.keys(this.termsMessages()));
    return this.availableLocales().filter((l) => !used.has(l));
  });

  protected unusedDenyLocales = computed(() => {
    const used = new Set(Object.keys(this.denyMessages()));
    return this.availableLocales().filter((l) => !used.has(l));
  });

  localizedCountryCodes = computed(() => {
    return COUNTRY_CODES.map((code) => ({
      code,
      name: this.intlDisplayNames.of(code) || code,
    })).sort((a, b) => a.name.localeCompare(b.name));
  });

  isRealmAdmin = signal(false);
  me = signal<UserMe | null>(null);
  // Full per-client permission list from /me, used to check ia_users on clients other than the one being edited
  private accessibleClients: {
    id?: string;
    clientId?: string;
    canManage: boolean;
    permissions?: string[];
  }[] = [];

  openIdConfigurationUrl: string | null =
    environment.auth.issuer + '/.well-known/openid-configuration';
  samlConfigurationUrl: string | null = environment.auth.issuer + '/protocol/saml/descriptor';

  private keycloakApi = inject(KeycloakAdminApi);

  constructor() {
    this.clientForm = this.fb.group({
      general: this.fb.group({
        clientId: ['', Validators.required],
        name: [''],
        description: [''],
        enabled: [true],
        alwaysDisplayInConsole: [false],
      }),
      urls: this.fb.group({
        rootUrl: [''],
        adminUrl: [''],
        homeUrl: [''],
        redirectUris: this.fb.array([]),
        webOrigins: this.fb.array([]),
      }),
      auth: this.fb.group({
        clientAuthenticatorType: ['client-secret'],
        publicClient: [false],
        bearerOnly: [false],
        consentRequired: [false],
        frontchannelLogout: [false],
      }),
      audienceMapper: this.fb.group({
        enabled: [false],
      }),
      flows: this.fb.group({
        standardFlowEnabled: [true],
        implicitFlowEnabled: [false],
        directAccessGrantsEnabled: [false],
        serviceAccountsEnabled: [false],
        authorizationServicesEnabled: [false],
      }),
      attributes: this.fb.group({
        salesforce: this.fb.group({
          campaignId: [''],
        }),
        hideSocialProviders: [false],
        logoutUrls: this.fb.array([]),
        ebm_specific_terms_key: [''],
        ebm_specific_terms_date: [''],
        custom: this.fb.array([]),
      }),
      denyAccess: this.fb.group({
        enabled: [false],
        userType: [''],
        anyClientRole: [''],
        roles: [['']],
        rolesMode: [''],
        countries: [''],
        customAttributeFilters: this.fb.array([]),
        messageKey: [''],
        redirectUri: [''],
      }),
    });

    this.roleForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    // isRealmAdmin is authoritative from the backend, not derived from JWT roles here
    const detailPermissions = await this.keycloakApi.getMe();
    this.isRealmAdmin.set(detailPermissions.isRealmAdmin);
    this.accessibleClients = detailPermissions.accessibleClients || [];
    this.loadAvailableLocales();
    this.loadClientData();
  }

  setActiveTab(tabEvent: Event): void {
    this.activeTab = (tabEvent as CustomEvent)?.detail?.activeTab;

    if (this.activeTab === 'attributes' && this.clientId && this.termsEnabled()) {
      if (Object.keys(this.termsMessages()).length === 0 && !this.isLoadingTermsMessages()) {
        this.loadTermsMessages();
      }
    }
    if (this.activeTab === 'deny-access' && this.clientId && this.denyMessageEnabled()) {
      if (Object.keys(this.denyMessages()).length === 0 && !this.isLoadingDenyMessages()) {
        this.loadDenyMessages();
      }
    }
    if (
      this.activeTab === 'service-account-roles' &&
      this.clientId &&
      !this.serviceAccountTabInitialized
    ) {
      this.serviceAccountTabInitialized = true;
      this.initServiceAccountRolesTab();
    }
    if (
      this.activeTab === 'roles' &&
      this.clientId &&
      !this.otherServiceAccountClientsTabInitialized
    ) {
      this.otherServiceAccountClientsTabInitialized = true;
      this.loadInitialOtherServiceAccountClients();
    }
  }

  // Getters for form arrays
  get redirectUrisArray(): FormArray<FormControl> {
    return this.clientForm.get('urls.redirectUris') as FormArray;
  }

  get webOriginsArray(): FormArray<FormControl> {
    return this.clientForm.get('urls.webOrigins') as FormArray;
  }

  get customAttributesArray(): FormArray<FormGroup> {
    return this.clientForm.get('attributes.custom') as FormArray;
  }

  get logoutUrlsArray(): FormArray<FormControl> {
    return this.clientForm.get('attributes.logoutUrls') as FormArray;
  }

  get authGroup(): FormGroup {
    return this.clientForm.get('auth') as FormGroup;
  }

  get customAttributeFiltersArray(): FormArray<FormGroup> {
    return this.clientForm.get('denyAccess.customAttributeFilters') as FormArray;
  }

  // Methods to handle redirect URIs
  addRedirectUri(value = ''): void {
    this.redirectUrisArray.push(new FormControl(value));
  }

  removeRedirectUri(index: number): void {
    this.redirectUrisArray.removeAt(index);
  }

  // Methods to handle web origins
  addWebOrigin(value = ''): void {
    this.webOriginsArray.push(new FormControl(value));
  }

  removeWebOrigin(index: number): void {
    this.webOriginsArray.removeAt(index);
  }

  addLogoutUrl(value = '') {
    this.logoutUrlsArray.push(new FormControl(value));
  }
  removeLogoutUrl(index: number): void {
    this.logoutUrlsArray.removeAt(index);
  }

  // Methods to handle custom attributes
  addCustomAttribute(key = '', value = ''): void {
    this.customAttributesArray.push(
      this.fb.group({
        key: [key],
        value: [value],
      })
    );
  }

  removeCustomAttribute(index: number): void {
    this.customAttributesArray.removeAt(index);
  }

  getAttribute(index: number, field: string): FormControl {
    return this.customAttributesArray.at(index).get(field) as FormControl;
  }

  // Methods to handle custom attribute filters
  addCustomAttributeFilter(attributeName = '', regex = ''): void {
    this.customAttributeFiltersArray.push(
      this.fb.group({
        attributeName: [attributeName],
        regex: [regex],
      })
    );
  }

  removeCustomAttributeFilter(index: number): void {
    this.customAttributeFiltersArray.removeAt(index);
  }

  getCustomAttributeFilter(index: number, field: string): FormControl {
    return this.customAttributeFiltersArray.at(index).get(field) as FormControl;
  }

  loadClientData(): void {
    this.clientId = this.route.snapshot.params['id'];

    if (!this.clientId) {
      return;
    }
    this.isLoading = true;
    this.keycloakApi
      .getClientDetails(this.clientId)
      .then((data) => {
        this.client = data;

        // Clear form arrays before populating
        while (this.redirectUrisArray.length) {
          this.redirectUrisArray.removeAt(0);
        }
        while (this.webOriginsArray.length) {
          this.webOriginsArray.removeAt(0);
        }
        while (this.customAttributesArray.length) {
          this.customAttributesArray.removeAt(0);
        }

        // Populate form with client data
        const generalGroup = this.clientForm.get('general');
        generalGroup?.patchValue({
          clientId: data.clientId || '',
          name: data.name || '',
          description: data.description || '',
          enabled: data.enabled !== false, // Default to true if undefined
          alwaysDisplayInConsole: data.alwaysDisplayInConsole === true,
        });

        const urlsGroup = this.clientForm.get('urls');
        urlsGroup?.patchValue({
          rootUrl: data.rootUrl || '',
          adminUrl: data.adminUrl || '',
          homeUrl: data.baseUrl || '',
        });

        // Populate redirect URIs
        if (data.redirectUris && data.redirectUris.length) {
          data.redirectUris.forEach((uri) => this.addRedirectUri(uri));
        } else {
          this.addRedirectUri();
        }

        // Populate web origins
        if (data.webOrigins && data.webOrigins.length) {
          data.webOrigins.forEach((origin) => this.addWebOrigin(origin));
        } else {
          this.addWebOrigin();
        }

        const authGroup = this.clientForm.get('auth');
        authGroup?.patchValue({
          clientAuthenticatorType: data.clientAuthenticatorType || 'client-secret',
          publicClient: data.publicClient === true,
          bearerOnly: data.bearerOnly === true,
          consentRequired: data.consentRequired === true,
          frontchannelLogout: data.frontchannelLogout === true,
        });

        const flowsGroup = this.clientForm.get('flows');
        flowsGroup?.patchValue({
          standardFlowEnabled: data.standardFlowEnabled !== false, // Default to true if undefined
          implicitFlowEnabled: data.implicitFlowEnabled === true,
          directAccessGrantsEnabled: data.directAccessGrantsEnabled === true,
          serviceAccountsEnabled: data.serviceAccountsEnabled === true,
          authorizationServicesEnabled: data.authorizationServicesEnabled === true,
        });

        // Populate attributes
        if (data.attributes) {
          // Salesforce specific attributes
          const salesforceGroup = this.clientForm.get('attributes.salesforce');
          salesforceGroup?.patchValue({
            campaignId: data.attributes['ebm_base_campaign_ids'] || '',
          });

          // Define known attributes that have specific UI fields
          const knownAttributes = new Set([
            'ebm_base_campaign_ids',
            'hideSocialProviders',
            'ebm_specific_terms_key',
            'ebm_specific_terms_date',
            'post.logout.redirect.uris',
            'identity-admin.dev-admin-group',
            'identity-admin.user-admin-group',
            'access.requires.userType',
            'access.requires.anyClientRole',
            'access.requires.roles',
            'access.requires.roles.mode',
            'access.requires.countries',
            'access.requires.attributes',
            'access.deny.messageKey',
            'access.deny.redirectUri',
          ]);

          // Add other custom attributes (only those not already handled by specific fields)
          Object.entries(data.attributes).forEach(([key, value]) => {
            if (!knownAttributes.has(key)) {
              this.addCustomAttribute(key, value);
            }
          });

          this.clientForm
            .get('attributes.hideSocialProviders')
            ?.setValue(data.attributes['hideSocialProviders'] === 'true');
          this.clientForm
            .get('attributes.ebm_specific_terms_key')
            ?.setValue(data.attributes['ebm_specific_terms_key']);
          this.clientForm
            .get('attributes.ebm_specific_terms_date')
            ?.setValue(data.attributes['ebm_specific_terms_date']);

          // Determine if terms localization is enabled
          const termsKey = data.attributes['ebm_specific_terms_key'];
          this.termsEnabled.set(!!termsKey && termsKey.trim() !== '');
          if (termsKey) {
            this.termsLocalizationKey.set(termsKey);
          }
          const logoutUrls = data.attributes['post.logout.redirect.uris']?.split('##') || [''];
          const logoutFormArray = this.clientForm.get('attributes.logoutUrls') as FormArray;
          // Clear existing entries
          while (logoutFormArray.length) {
            logoutFormArray.removeAt(0);
          }
          // Populate with current values or add an empty one
          logoutUrls.forEach((url) => this.addLogoutUrl(url));
          if (logoutUrls.length === 0) {
            this.addLogoutUrl();
          }
        }

        // Get current user ID from OAuth token
        const claims = this.oauthService.getIdentityClaims() as { sub?: string };
        if (claims?.sub) {
          this.currentUserId.set(claims.sub);
        }

        // Check if deny access scope is enabled for this client
        this.clientForm
          .get('denyAccess.enabled')
          ?.setValue(data.defaultClientScopes?.includes(environment.denyAccessScope) === true);

        // Check if the self-audience protocol mapper is enabled for this client
        this.clientForm
          .get('audienceMapper.enabled')
          ?.setValue(this.findAudienceMapper(data)?.name === AUDIENCE_MAPPER_NAME);

        // Populate deny access attributes
        const denyAccessGroup = this.clientForm.get('denyAccess');
        const rolesArray =
          data.attributes?.['access.requires.roles']?.split(',').filter((r) => r.trim() !== '') ||
          [];

        // Clear custom attribute filters array
        while (this.customAttributeFiltersArray.length) {
          this.customAttributeFiltersArray.removeAt(0);
        }

        // Parse custom attribute filters from JSON
        const customAttributesJson = data.attributes?.['access.requires.attributes'] || '{}';
        try {
          const customAttributesObj = JSON.parse(customAttributesJson);
          Object.entries(customAttributesObj).forEach(([key, value]) => {
            this.addCustomAttributeFilter(key, value as string);
          });
        } catch (error) {
          console.error('Failed to parse custom attribute filters:', error);
        }

        // Add empty row if none exist
        if (this.customAttributeFiltersArray.length === 0) {
          this.addCustomAttributeFilter();
        }

        denyAccessGroup?.patchValue({
          userType: data.attributes?.['access.requires.userType'] || '',
          anyClientRole: data.attributes?.['access.requires.anyClientRole'] || '',
          roles: rolesArray,
          rolesMode: data.attributes?.['access.requires.roles.mode'] || '',
          countries: data.attributes?.['access.requires.countries']?.split(',') || [],
          messageKey: data.attributes?.['access.deny.messageKey'] || '',
          redirectUri: data.attributes?.['access.deny.redirectUri'] || '',
        });

        // Determine if deny message localization is enabled
        const denyKey = data.attributes?.['access.deny.messageKey'];
        this.denyMessageEnabled.set(!!denyKey && denyKey.trim() !== '');
        if (denyKey) {
          this.denyLocalizationKey.set(denyKey);
        }

        this.isLoading = false;
      })
      .then(() => {
        if (this.isRealmAdmin()) {
          return this.me.set({
            // has all permissions
            canManage: true,
            permissions: [
              'ia_full_admin',
              'ia_client_admin',
              'ia_basics',
              'ia_salesforce',
              'ia_terms',
              'ia_urls',
              'ia_denyconfig',
              'ia_users',
            ],
          });
          // apply permissions not necessary
        } else {
          return this.keycloakApi.getMe().then((fullMe: any) => {
            // get user permissions
            this.me.set(
              fullMe.accessibleClients?.find(
                (ac: { clientId: string }) => ac.clientId === this.client!.clientId
              ) || null
            );
            this.applyPermissions();
          });
        }
      })
      .then(() => this.loadClientRolesAndPermissions())
      .catch((error) => {
        this.errorMessage = `Failed to load client data: ${error}`;
        this.isLoading = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  private findAudienceMapper(
    client: ClientRepresentation | null
  ): { id?: string; name?: string } | undefined {
    return client?.protocolMappers?.find(
      (mapper) =>
        mapper.name === AUDIENCE_MAPPER_NAME && mapper.protocolMapper === 'oidc-audience-mapper'
    );
  }

  applyPermissions(): void {
    if (this.isRealmAdmin() || this.me()?.canManage) {
      return; // keep everything enabled
    }
    const perms = this.me()!.permissions ?? [];
    if (!perms.includes('ia_basics')) {
      this.clientForm.get('general.name')!.disable();
      this.clientForm.get('general.description')!.disable();
    }
    if (!perms.includes('ia_urls')) {
      this.clientForm.get('urls.rootUrl')!.disable();
      this.clientForm.get('urls.adminUrl')!.disable();
      this.clientForm.get('urls.homeUrl')!.disable();
      this.clientForm.get('urls.redirectUris')!.disable();
      this.clientForm.get('urls.webOrigins')!.disable();
      this.logoutUrlsArray.disable();
    }
    if (!perms.includes('ia_salesforce')) {
      this.clientForm.get('attributes.salesforce.campaignId')!.disable();
    }
    if (!perms.includes('ia_terms')) {
      this.clientForm.get('attributes.ebm_specific_terms_date')!.disable();
    }
    if (!perms.includes('ia_denyconfig')) {
      this.clientForm.get('denyAccess.userType')!.disable();
      this.clientForm.get('denyAccess.anyClientRole')!.disable();
      this.clientForm.get('denyAccess.roles')!.disable();
      this.clientForm.get('denyAccess.rolesMode')!.disable();
      this.clientForm.get('denyAccess.countries')!.disable();
      this.customAttributeFiltersArray.disable();
      this.clientForm.get('denyAccess.messageKey')!.disable();
      this.clientForm.get('denyAccess.redirectUri')!.disable();
    }
  }

  saveClientAttributes(): void {
    console.log('Saving client attributes...');
    if (this.clientForm.invalid || !this.clientId) {
      this.markFormGroupTouched(this.clientForm);
      return;
    }

    this.isSaving = true;

    const formValues = this.clientForm.getRawValue();

    // Build the client representation with the form values
    const updatedClientData: ClientRepresentation = {
      clientId: formValues.general.clientId,
      name: formValues.general.name,
      description: formValues.general.description,
      enabled: formValues.general.enabled,
      alwaysDisplayInConsole: formValues.general.alwaysDisplayInConsole,

      // URLs
      rootUrl: formValues.urls.rootUrl,
      adminUrl: formValues.urls.adminUrl,
      baseUrl: formValues.urls.homeUrl,
      redirectUris: formValues.urls.redirectUris.filter((uri: string) => uri.trim() !== ''),
      webOrigins: formValues.urls.webOrigins.filter((origin: string) => origin.trim() !== ''),

      // Authentication
      clientAuthenticatorType: formValues.auth.clientAuthenticatorType,
      publicClient: formValues.auth.publicClient,
      bearerOnly: formValues.auth.bearerOnly,
      consentRequired: formValues.auth.consentRequired,
      frontchannelLogout: formValues.auth.frontchannelLogout,

      // Flows
      standardFlowEnabled: formValues.flows.standardFlowEnabled,
      implicitFlowEnabled: formValues.flows.implicitFlowEnabled,
      directAccessGrantsEnabled: formValues.flows.directAccessGrantsEnabled,
      serviceAccountsEnabled: formValues.flows.serviceAccountsEnabled,
      authorizationServicesEnabled: formValues.flows.authorizationServicesEnabled,

      // Attributes
      attributes: {
        ebm_base_campaign_ids: formValues.attributes.salesforce.campaignId || '',
        hideSocialProviders: formValues.attributes.hideSocialProviders ? 'true' : 'false',
        ebm_specific_terms_key: this.termsEnabled() ? `client.${this.client?.clientId}.terms` : '',
        ebm_specific_terms_date: formValues.attributes.ebm_specific_terms_date,
        'post.logout.redirect.uris':
          (formValues.attributes.logoutUrls as string[])
            .filter((url) => url && url.trim() !== '')
            .join('##') || '',
        // Deny access attributes
        'access.requires.userType': formValues.denyAccess.userType || '',
        'access.requires.anyClientRole': formValues.denyAccess.anyClientRole || '',
        'access.requires.roles': formValues.denyAccess.roles.join(',') || '',
        'access.requires.roles.mode': formValues.denyAccess.rolesMode || '',
        'access.requires.countries': formValues.denyAccess.countries.join(',') || '',
        'access.requires.attributes': this.serializeCustomAttributeFilters(
          formValues.denyAccess.customAttributeFilters
        ),
        'access.deny.messageKey': this.denyMessageEnabled()
          ? `client.${this.client?.clientId}.deny.message`
          : '',
        'access.deny.redirectUri': formValues.denyAccess.redirectUri || '',
      },
    };

    // Add custom attributes (only those with both key and value)
    const customAttributes = formValues.attributes.custom as CustomAttribute[];
    customAttributes.forEach((attr) => {
      // Only add if key is not empty and value is provided
      // Skip if the key was already set by predefined attributes above
      if (
        attr.key &&
        attr.key.trim() !== '' &&
        updatedClientData.attributes![attr.key] === undefined
      ) {
        updatedClientData.attributes![attr.key] = attr.value || '';
      }
    });

    // Handle deny access scope assignment
    const denyAccessEnabled = formValues.denyAccess.enabled;
    const handleDenyAccessScope = async () => {
      try {
        // Only proceed if we need to do something
        const scopeAlreadyAssigned = this.client?.defaultClientScopes?.includes(
          environment.denyAccessScope
        );

        // Skip if nothing needs to be changed
        if (denyAccessEnabled === scopeAlreadyAssigned) {
          return;
        }

        // Get the deny access scope ID
        const denyAccessScope = await this.keycloakApi.getClientScopeByName(
          environment.denyAccessScope
        );

        if (!denyAccessScope || !denyAccessScope.id) {
          // Scope doesn't exist - log warning and continue
          console.warn(
            `Deny access scope '${environment.denyAccessScope}' not found. Skipping scope assignment.`
          );
          return;
        }

        if (!this.clientId) {
          console.warn('Client ID missing. Skipping scope assignment.');
          return;
        }

        if (denyAccessEnabled) {
          // Add deny access scope if not already assigned
          await this.keycloakApi.addDefaultClientScope(this.clientId, denyAccessScope.id);
        } else {
          // Remove deny access scope if assigned
          await this.keycloakApi.removeDefaultClientScope(this.clientId, denyAccessScope.id);
        }
      } catch (error) {
        // Log the error but don't fail the save operation
        console.error(`Failed to manage deny access scope: ${error}`);
        // Optionally show a warning to the user but don't fail
        this.errorMessage = `Client saved, but failed to manage deny access scope. ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      }
    };

    // Handle self-audience protocol mapper creation/removal
    const audienceMapperEnabled = formValues.audienceMapper.enabled;
    const handleAudienceMapper = async () => {
      try {
        const existingMapper = this.findAudienceMapper(this.client);

        // Skip if nothing needs to be changed
        if (audienceMapperEnabled === !!existingMapper) {
          return;
        }

        if (!this.clientId) {
          console.warn('Client ID missing. Skipping audience mapper assignment.');
          return;
        }

        if (audienceMapperEnabled) {
          await this.keycloakApi.createProtocolMapper(this.clientId, {
            name: AUDIENCE_MAPPER_NAME,
            protocol: 'openid-connect',
            protocolMapper: 'oidc-audience-mapper',
            consentRequired: false,
            config: {
              'included.client.audience': this.client?.clientId || '',
              'id.token.claim': 'false',
              'access.token.claim': 'true',
            },
          });
        } else if (existingMapper?.id) {
          await this.keycloakApi.deleteProtocolMapper(this.clientId, existingMapper.id);
        }
      } catch (error) {
        // Log the error but don't fail the save operation
        console.error(`Failed to manage audience mapper: ${error}`);
        this.errorMessage = `Client saved, but failed to manage audience mapper. ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      }
    };

    this.keycloakApi
      .updateClientAttributes(this.clientId, updatedClientData)
      .then(() => handleDenyAccessScope())
      .then(() => handleAudienceMapper())
      .then(() => this.saveAllLocalizations())
      .then(() => this.saveAllPermissions())
      .then(() => {
        this.successMessage = 'Client configuration updated successfully';
        this.isSaving = false;
        setTimeout(() => (this.successMessage = null), 3000);
        // Refresh client data to get the latest state
        this.loadClientData();
      })
      .catch((error) => {
        this.errorMessage = `Failed to update client configuration: ${error}`;
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Helper to mark all form controls as touched
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control: AbstractControl) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        for (let i = 0; i < control.length; i++) {
          const arrayControl = control.at(i);
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        }
      } else {
        control.markAsTouched();
      }

      control.updateValueAndValidity();
    });
  }

  // Role management methods
  loadClientRolesAndPermissions(): void {
    if (!this.clientId) return;

    this.isLoadingRoles = true;
    this.keycloakApi
      .getClientRoles(this.clientId)
      .then((roles) => {
        this.clientRoles = roles;
        this.isLoadingRoles = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load client roles: ${error}`;
        this.isLoadingRoles = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });

    this.isLoadingPermissions = true;
    this.keycloakApi
      .getClientPermissions(this.client!.clientId!)
      .then((permissions) => {
        this.clientPermissions = permissions.sort((p1, p2) => p1.user.localeCompare(p2.user));
        this.isLoadingPermissions = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load client permissions: ${error}`;
        this.isLoadingPermissions = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  openCreateRoleModal(): void {
    this.roleForm.reset({
      name: '',
      description: '',
    });
    this.showCreateRoleModal = true;
  }

  openEditRoleModal(role: RoleRepresentation): void {
    this.selectedRole = role;
    this.roleForm.patchValue({
      name: role.name,
      description: role.description,
    });
    this.showEditRoleModal = true;
  }

  openDeleteRoleModal(role: RoleRepresentation): void {
    this.selectedRole = role;
    this.showDeleteRoleModal = true;
  }

  closeRoleModal(): void {
    this.showCreateRoleModal = false;
    this.showEditRoleModal = false;
    this.showDeleteRoleModal = false;
    this.selectedRole = null;
  }

  createRole(): void {
    if (this.roleForm.invalid || !this.clientId) {
      return;
    }

    this.isSavingRole = true;
    const newRole: RoleRepresentation = {
      name: this.roleForm.value.name,
      description: this.roleForm.value.description,
    };

    this.keycloakApi
      .createClientRole(this.clientId, newRole)
      .then(() => {
        this.successMessage = `Role "${newRole.name}" created successfully`;
        this.closeRoleModal();
        this.loadClientRolesAndPermissions();
        this.isSavingRole = false;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to create role: ${error}`;
        this.isSavingRole = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  updateRole(): void {
    if (this.roleForm.invalid || !this.clientId || !this.selectedRole?.name) {
      return;
    }

    this.isSavingRole = true;
    const updatedRole: RoleRepresentation = {
      name: this.roleForm.value.name,
      description: this.roleForm.value.description,
    };

    this.keycloakApi
      .updateClientRole(this.clientId, this.selectedRole.name, updatedRole)
      .then(() => {
        this.successMessage = `Role "${updatedRole.name}" updated successfully`;
        this.closeRoleModal();
        this.loadClientRolesAndPermissions();
        this.isSavingRole = false;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to update role: ${error}`;
        this.isSavingRole = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  deleteRole(): void {
    if (!this.clientId || !this.selectedRole?.name) {
      return;
    }

    this.isSavingRole = true;
    this.keycloakApi
      .deleteClientRole(this.clientId, this.selectedRole.name)
      .then(() => {
        this.successMessage = `Role "${this.selectedRole?.name}" deleted successfully`;
        this.closeRoleModal();
        this.loadClientRolesAndPermissions();
        this.isSavingRole = false;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to delete role: ${error}`;
        this.isSavingRole = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Composite role membership management

  openManageCompositesModal(role: RoleRepresentation): void {
    this.compositeRoleTarget.set(role);
    this.compositeMembers.set([]);
    this.compositeClientSearchResults.set([]);
    this.compositeClientResultsTruncated.set(false);
    this.selectedCompositeClient.set(null);
    this.compositeCandidateRoles.set([]);
    this.selectedCompositeRoleToAdd.set(null);
    this.showManageCompositesModal.set(true);
    this.loadCompositeMembers();
    this.loadInitialCompositeClients().then(() => {
      if (!this.client?.id) {
        return;
      }
      // Defer so the ep-option elements have rendered before the combobox looks up the label for this value
      setTimeout(() => {
        this.selectedCompositeClient.set(this.client);
        this.loadCompositeCandidateRolesForClient(this.client!.id!);
      }, 50);
    });
  }

  closeManageCompositesModal(): void {
    this.showManageCompositesModal.set(false);
    this.compositeRoleTarget.set(null);
    this.compositeMembers.set([]);
    this.compositeClientSearchResults.set([]);
    this.compositeClientResultsTruncated.set(false);
    this.selectedCompositeClient.set(null);
    this.compositeCandidateRoles.set([]);
    this.selectedCompositeRoleToAdd.set(null);
  }

  private loadCompositeMembers(): void {
    const role = this.compositeRoleTarget();
    if (!this.clientId || !role?.name) {
      return;
    }
    this.isLoadingComposites.set(true);
    this.keycloakApi
      .getClientRoleComposites(this.clientId, role.name)
      .then((members) => this.compositeMembers.set(members))
      .catch((error) => {
        this.errorMessage = `Failed to load composite members: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isLoadingComposites.set(false));
  }

  private static readonly COMPOSITE_CLIENT_PAGE_SIZE = 20;

  /**
   * Loads the first page of clients the user can manage, shown before any search is typed.
   * The current client is always included so it can be pre-selected as the default.
   */
  private loadInitialCompositeClients(): Promise<void> {
    return this.keycloakApi
      .getClientsPaginated(undefined, 0, ClientAttributesForm.COMPOSITE_CLIENT_PAGE_SIZE)
      .then((clients) => this.setCompositeClientSearchResults(clients, true))
      .catch((error) => {
        console.error('Error loading initial clients:', error);
        this.compositeClientSearchResults.set(this.client ? [this.client] : []);
        this.compositeClientResultsTruncated.set(false);
      });
  }

  private setCompositeClientSearchResults(
    clients: ClientRepresentation[],
    includeCurrentClient = false
  ): void {
    this.compositeClientResultsTruncated.set(
      clients.length >= ClientAttributesForm.COMPOSITE_CLIENT_PAGE_SIZE
    );
    const manageable = clients.filter((c) => c.access?.manage);
    if (includeCurrentClient && this.client && !manageable.some((c) => c.id === this.client!.id)) {
      manageable.unshift(this.client);
    }
    // Keep the currently selected client in the option list so the combobox can still
    // resolve its display label after a subsequent search replaces the result set.
    const selected = this.selectedCompositeClient();
    if (selected && selected !== 'REALM' && !manageable.some((c) => c.id === selected.id)) {
      manageable.unshift(selected);
    }
    this.compositeClientSearchResults.set(manageable);
  }

  /**
   * Search manageable clients for the composite member picker, with debouncing
   */
  searchCompositeClients(event: Event): void {
    if (this.compositeClientSearchTimeout) {
      clearTimeout(this.compositeClientSearchTimeout);
    }

    const searchCombobox = event?.target;
    const searchTerm = (event as CustomEvent)?.detail?.query || '';

    if (searchTerm.length < 2) {
      this.loadInitialCompositeClients();
      return;
    }

    this.compositeClientSearchTimeout = setTimeout(async () => {
      try {
        const clients = await this.keycloakApi.getClientsPaginated(
          searchTerm,
          0,
          ClientAttributesForm.COMPOSITE_CLIENT_PAGE_SIZE
        );
        this.setCompositeClientSearchResults(clients);
      } catch (error) {
        console.error('Error searching clients:', error);
        this.compositeClientSearchResults.set([]);
        this.compositeClientResultsTruncated.set(false);
      } finally {
        setTimeout(() => {
          (searchCombobox as any)?.updateOptionsDisplay(); // eslint-disable-line @typescript-eslint/no-explicit-any
        }, 50);
      }
    }, 300);
  }

  /**
   * Handle client (or "Realm") selection from combobox — loads its roles as add-candidates
   */
  async onCompositeClientSelected(event: Event): Promise<void> {
    const value = (event as CustomEvent).detail?.value;
    this.selectedCompositeRoleToAdd.set(null);
    this.compositeCandidateRoles.set([]);

    if (!value) {
      this.selectedCompositeClient.set(null);
      return;
    }

    if (value === 'REALM') {
      this.selectedCompositeClient.set('REALM');
      try {
        const roles = await this.keycloakApi.getRealmRoles();
        this.compositeCandidateRoles.set(this.filterOutExistingComposites(roles));
      } catch (error) {
        console.error('Error loading realm roles:', error);
      }
      return;
    }

    const client = this.compositeClientSearchResults().find((c) => c.id === value) || null;
    this.selectedCompositeClient.set(client);
    if (!client?.id) {
      return;
    }

    await this.loadCompositeCandidateRolesForClient(client.id);
  }

  private async loadCompositeCandidateRolesForClient(clientId: string): Promise<void> {
    try {
      const roles = await this.keycloakApi.getClientRoles(clientId);
      this.compositeCandidateRoles.set(this.filterOutExistingComposites(roles));
    } catch (error) {
      console.error('Error loading client roles:', error);
    }
  }

  private filterOutExistingComposites(roles: RoleRepresentation[]): RoleRepresentation[] {
    const target = this.compositeRoleTarget();
    const existingIds = new Set(this.compositeMembers().map((m) => m.id));
    return roles.filter((r) => r.id !== target?.id && !existingIds.has(r.id));
  }

  onCompositeRoleToAddSelected(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    const role = this.compositeCandidateRoles().find((r) => r.id === value) || null;
    this.selectedCompositeRoleToAdd.set(role);
  }

  addCompositeMember(): void {
    const target = this.compositeRoleTarget();
    const roleToAdd = this.selectedCompositeRoleToAdd();
    if (!this.clientId || !target?.name || !roleToAdd) {
      return;
    }

    this.isSavingComposite.set(true);
    this.keycloakApi
      .addClientRoleComposites(this.clientId, target.name, [roleToAdd])
      .then(() => {
        this.selectedCompositeRoleToAdd.set(null);
        this.loadCompositeMembers();
        this.loadClientRolesAndPermissions();
        this.successMessage = `Added "${roleToAdd.name}" as a composite member`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to add composite member: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isSavingComposite.set(false));
  }

  removeCompositeMember(member: RoleRepresentation): void {
    const target = this.compositeRoleTarget();
    if (!this.clientId || !target?.name) {
      return;
    }

    this.isSavingComposite.set(true);
    this.keycloakApi
      .removeClientRoleComposites(this.clientId, target.name, [member])
      .then(() => {
        this.loadCompositeMembers();
        this.loadClientRolesAndPermissions();
        this.successMessage = `Removed "${member.name}" from composite role`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove composite member: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isSavingComposite.set(false));
  }

  // Service account role assignment

  private initServiceAccountRolesTab(): void {
    this.serviceAccountScope.set(null);
    this.serviceAccountClientSearchResults.set([]);
    this.serviceAccountClientResultsTruncated.set(false);
    this.serviceAccountAssignedRoles.set([]);
    this.serviceAccountAvailableRoles.set([]);
    this.serviceAccountEffectiveRoles.set([]);
    this.serviceAccountAllRoleGroups.set([]);
    this.selectedServiceAccountRoleToAdd.set(null);
    this.loadServiceAccountAllRoleGroups();
    this.loadInitialServiceAccountClients().then(() => {
      if (!this.client?.id) {
        return;
      }
      // Defer so the ep-option elements have rendered before the combobox looks up the label for this value
      setTimeout(() => {
        this.serviceAccountScope.set(this.client);
        this.loadServiceAccountRolesForScope();
      }, 50);
    });
  }

  private static readonly SERVICE_ACCOUNT_CLIENT_PAGE_SIZE = 20;

  /**
   * Loads the first page of clients the user can manage, shown before any search is typed.
   * The current client is always included so it can be pre-selected as the default.
   */
  private loadInitialServiceAccountClients(): Promise<void> {
    return this.keycloakApi
      .getClientsPaginated(undefined, 0, ClientAttributesForm.SERVICE_ACCOUNT_CLIENT_PAGE_SIZE)
      .then((clients) => this.setServiceAccountClientSearchResults(clients, true))
      .catch((error) => {
        console.error('Error loading initial clients:', error);
        this.serviceAccountClientSearchResults.set(this.client ? [this.client] : []);
        this.serviceAccountClientResultsTruncated.set(false);
      });
  }

  private setServiceAccountClientSearchResults(
    clients: ClientRepresentation[],
    includeCurrentClient = false
  ): void {
    this.serviceAccountClientResultsTruncated.set(
      clients.length >= ClientAttributesForm.SERVICE_ACCOUNT_CLIENT_PAGE_SIZE
    );
    const manageable = clients.filter((c) => c.access?.manage);
    if (includeCurrentClient && this.client && !manageable.some((c) => c.id === this.client!.id)) {
      manageable.unshift(this.client);
    }
    // Keep the currently selected scope client in the option list so the combobox can still
    // resolve its display label after a subsequent search replaces the result set.
    const selected = this.serviceAccountScope();
    if (selected && selected !== 'REALM' && !manageable.some((c) => c.id === selected.id)) {
      manageable.unshift(selected);
    }
    this.serviceAccountClientSearchResults.set(manageable);
  }

  /**
   * Search manageable clients to pick roles from, with debouncing
   */
  searchServiceAccountClients(event: Event): void {
    if (this.serviceAccountClientSearchTimeout) {
      clearTimeout(this.serviceAccountClientSearchTimeout);
    }

    const searchCombobox = event?.target;
    const searchTerm = (event as CustomEvent)?.detail?.query || '';

    if (searchTerm.length < 2) {
      this.loadInitialServiceAccountClients();
      return;
    }

    this.serviceAccountClientSearchTimeout = setTimeout(async () => {
      try {
        const clients = await this.keycloakApi.getClientsPaginated(
          searchTerm,
          0,
          ClientAttributesForm.SERVICE_ACCOUNT_CLIENT_PAGE_SIZE
        );
        this.setServiceAccountClientSearchResults(clients);
      } catch (error) {
        console.error('Error searching clients:', error);
        this.serviceAccountClientSearchResults.set([]);
        this.serviceAccountClientResultsTruncated.set(false);
      } finally {
        setTimeout(() => {
          (searchCombobox as any)?.updateOptionsDisplay(); // eslint-disable-line @typescript-eslint/no-explicit-any
        }, 50);
      }
    }, 300);
  }

  /**
   * Handle client (or "Realm") selection from combobox — loads that scope's role mappings
   */
  onServiceAccountScopeSelected(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    this.selectedServiceAccountRoleToAdd.set(null);
    this.serviceAccountAvailableRoles.set([]);
    this.serviceAccountAssignedRoles.set([]);
    this.serviceAccountEffectiveRoles.set([]);

    if (!value) {
      this.serviceAccountScope.set(null);
      return;
    }

    if (value === 'REALM') {
      this.serviceAccountScope.set('REALM');
      this.loadServiceAccountRolesForScope();
      return;
    }

    const client = this.serviceAccountClientSearchResults().find((c) => c.id === value) || null;
    this.serviceAccountScope.set(client);
    if (client) {
      this.loadServiceAccountRolesForScope();
    }
  }

  private loadServiceAccountRolesForScope(): void {
    const scope = this.serviceAccountScope();
    if (!this.clientId || !scope) {
      return;
    }
    const roleClientId = scope === 'REALM' ? 'REALM' : scope.id;
    if (!roleClientId) {
      return;
    }

    this.isLoadingServiceAccountRoles.set(true);
    Promise.all([
      this.keycloakApi.getAvailableServiceAccountRoleMappings(this.clientId, roleClientId),
      this.keycloakApi.getServiceAccountRoleMappings(this.clientId, roleClientId),
      this.keycloakApi.getEffectiveServiceAccountRoleMappings(this.clientId, roleClientId),
    ])
      .then(([available, assigned, effective]) => {
        this.serviceAccountAvailableRoles.set(available);
        this.serviceAccountAssignedRoles.set(assigned);
        this.serviceAccountEffectiveRoles.set(effective);
      })
      .catch((error) => {
        this.errorMessage = `Failed to load service account roles: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isLoadingServiceAccountRoles.set(false));
  }

  onServiceAccountRoleToAddSelected(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    const role = this.serviceAccountAvailableRoles().find((r) => r.id === value) || null;
    this.selectedServiceAccountRoleToAdd.set(role);
  }

  /**
   * Loads every role assigned to this client's service account, grouped by granting
   * client/realm — independent of whichever scope is currently selected in the picker.
   */
  private loadServiceAccountAllRoleGroups(): void {
    if (!this.clientId) {
      return;
    }
    this.isLoadingServiceAccountAllRoles.set(true);
    this.keycloakApi
      .getAllServiceAccountRoleMappings(this.clientId)
      .then((groups) => this.serviceAccountAllRoleGroups.set(groups))
      .catch((error) => {
        this.errorMessage = `Failed to load all assigned roles: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isLoadingServiceAccountAllRoles.set(false));
  }

  assignServiceAccountRole(): void {
    const scope = this.serviceAccountScope();
    const role = this.selectedServiceAccountRoleToAdd();
    if (!this.clientId || !scope || !role) {
      return;
    }
    const roleClientId = scope === 'REALM' ? 'REALM' : scope.id;
    if (!roleClientId) {
      return;
    }

    this.isSavingServiceAccountRole.set(true);
    this.keycloakApi
      .assignServiceAccountRole(this.clientId, roleClientId, role)
      .then(() => {
        this.selectedServiceAccountRoleToAdd.set(null);
        this.serviceAccountAvailableRoles.set(
          this.serviceAccountAvailableRoles().filter((r) => r.id !== role.id)
        );
        this.serviceAccountAssignedRoles.set([...this.serviceAccountAssignedRoles(), role]);
        if (!this.serviceAccountEffectiveRoles().find((r) => r.id === role.id)) {
          this.serviceAccountEffectiveRoles.set([...this.serviceAccountEffectiveRoles(), role]);
        }
        this.successMessage = `Assigned role "${role.name}" to the service account`;
        setTimeout(() => (this.successMessage = null), 3000);
        this.loadServiceAccountAllRoleGroups();
      })
      .catch((error) => {
        this.errorMessage = `Failed to assign role: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isSavingServiceAccountRole.set(false));
  }

  removeServiceAccountRole(role: RoleRepresentation): void {
    const scope = this.serviceAccountScope();
    if (!this.clientId || !scope) {
      return;
    }
    const roleClientId = scope === 'REALM' ? 'REALM' : scope.id;
    if (!roleClientId) {
      return;
    }

    this.isSavingServiceAccountRole.set(true);
    this.keycloakApi
      .removeServiceAccountRole(this.clientId, roleClientId, role)
      .then(() => {
        this.serviceAccountAssignedRoles.set(
          this.serviceAccountAssignedRoles().filter((r) => r.id !== role.id)
        );
        this.serviceAccountAvailableRoles.set([...this.serviceAccountAvailableRoles(), role]);
        this.successMessage = `Removed role "${role.name}" from the service account`;
        setTimeout(() => (this.successMessage = null), 3000);
        // Effective roles may still include it via a composite, so re-fetch rather than assume
        if (this.clientId) {
          this.keycloakApi
            .getEffectiveServiceAccountRoleMappings(this.clientId, roleClientId)
            .then((effective) => this.serviceAccountEffectiveRoles.set(effective));
        }
        this.loadServiceAccountAllRoleGroups();
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove role: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isSavingServiceAccountRole.set(false));
  }

  // Assign one of this client's own roles to another client's service account

  /** Mirrors the backend's ia_users check (handleServiceAccountRoleAssignment) rather than canManage. */
  private hasIaUsersPermission(client: ClientRepresentation): boolean {
    if (this.isRealmAdmin()) {
      return true;
    }
    const entry = this.accessibleClients.find(
      (ac) => ac.id === client.id || ac.clientId === client.clientId
    );
    return !!entry?.permissions?.includes('ia_users');
  }

  /**
   * Loads the first page of clients with a service account that the user can manage,
   * shown before any search is typed. The current client is excluded.
   */
  private loadInitialOtherServiceAccountClients(): void {
    this.keycloakApi
      .getClientsPaginated(
        undefined,
        0,
        ClientAttributesForm.OTHER_SERVICE_ACCOUNT_CLIENT_PAGE_SIZE
      )
      .then((clients) => this.setOtherServiceAccountClientSearchResults(clients))
      .catch((error) => {
        console.error('Error loading initial clients:', error);
        this.otherServiceAccountClientResults.set([]);
        this.otherServiceAccountClientResultsTruncated.set(false);
      });
  }

  private setOtherServiceAccountClientSearchResults(clients: ClientRepresentation[]): void {
    this.otherServiceAccountClientResultsTruncated.set(
      clients.length >= ClientAttributesForm.OTHER_SERVICE_ACCOUNT_CLIENT_PAGE_SIZE
    );
    const eligible = clients.filter(
      (c) => c.serviceAccountsEnabled && this.hasIaUsersPermission(c) && c.id !== this.client?.id
    );
    // Keep the currently selected client in the option list so the combobox can still
    // resolve its display label after a subsequent search replaces the result set.
    const selected = this.selectedOtherServiceAccountClient();
    if (selected && !eligible.some((c) => c.id === selected.id)) {
      eligible.unshift(selected);
    }
    this.otherServiceAccountClientResults.set(eligible);
  }

  /**
   * Search clients with a service account that this user manages, with debouncing
   */
  searchOtherServiceAccountClients(event: Event): void {
    if (this.otherServiceAccountClientSearchTimeout) {
      clearTimeout(this.otherServiceAccountClientSearchTimeout);
    }

    const searchCombobox = event?.target;
    const searchTerm = (event as CustomEvent)?.detail?.query || '';

    if (searchTerm.length < 2) {
      this.loadInitialOtherServiceAccountClients();
      return;
    }

    this.otherServiceAccountClientSearchTimeout = setTimeout(async () => {
      try {
        const clients = await this.keycloakApi.getClientsPaginated(
          searchTerm,
          0,
          ClientAttributesForm.OTHER_SERVICE_ACCOUNT_CLIENT_PAGE_SIZE
        );
        this.setOtherServiceAccountClientSearchResults(clients);
      } catch (error) {
        console.error('Error searching clients:', error);
        this.otherServiceAccountClientResults.set([]);
        this.otherServiceAccountClientResultsTruncated.set(false);
      } finally {
        setTimeout(() => {
          (searchCombobox as any)?.updateOptionsDisplay(); // eslint-disable-line @typescript-eslint/no-explicit-any
        }, 50);
      }
    }, 300);
  }

  onOtherServiceAccountClientSelected(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    const client = this.otherServiceAccountClientResults().find((c) => c.id === value) || null;
    this.selectedOtherServiceAccountClient.set(client);
    this.roleGrantIsAssigned.set(false);
    this.checkRoleGrantStatus();
  }

  onRoleToGrantSelected(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    const role = this.clientRoles.find((r) => r.id === value) || null;
    this.selectedRoleToGrant.set(role);
    this.roleGrantIsAssigned.set(false);
    this.checkRoleGrantStatus();
  }

  private checkRoleGrantStatus(): void {
    const target = this.selectedOtherServiceAccountClient();
    const role = this.selectedRoleToGrant();
    if (!this.clientId || !target?.id || !role) {
      return;
    }

    this.isCheckingRoleGrantStatus.set(true);
    this.keycloakApi
      .getServiceAccountRoleMappings(target.id, this.clientId)
      .then((assigned) => {
        this.roleGrantIsAssigned.set(assigned.some((r) => r.id === role.id));
      })
      .catch((error) => {
        this.errorMessage = `Failed to check role assignment status: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isCheckingRoleGrantStatus.set(false));
  }

  assignRoleGrant(): void {
    const target = this.selectedOtherServiceAccountClient();
    const role = this.selectedRoleToGrant();
    if (!this.clientId || !target?.id || !role) {
      return;
    }

    this.isSavingRoleGrant.set(true);
    this.keycloakApi
      .assignServiceAccountRole(target.id, this.clientId, role)
      .then(() => {
        this.roleGrantIsAssigned.set(true);
        this.successMessage = `Assigned role "${role.name}" to the service account of "${target.name || target.clientId}"`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to assign role: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isSavingRoleGrant.set(false));
  }

  removeRoleGrant(): void {
    const target = this.selectedOtherServiceAccountClient();
    const role = this.selectedRoleToGrant();
    if (!this.clientId || !target?.id || !role) {
      return;
    }

    this.isSavingRoleGrant.set(true);
    this.keycloakApi
      .removeServiceAccountRole(target.id, this.clientId, role)
      .then(() => {
        this.roleGrantIsAssigned.set(false);
        this.successMessage = `Removed role "${role.name}" from the service account of "${target.name || target.clientId}"`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove role: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      })
      .finally(() => this.isSavingRoleGrant.set(false));
  }

  // Toggle client secret visibility
  toggleClientSecretVisibility(): void {
    this.showClientSecret.update((show) => !show);
  }

  // Admin management methods

  /**
   * Resolve a group path to its UUID by searching for the group
   */
  private async resolveGroupId(groupPath: string): Promise<string | null> {
    try {
      // Extract group name from path (last segment)
      const pathSegments = groupPath.split('/').filter((s) => s.length > 0);
      const groupName = pathSegments[pathSegments.length - 1];

      if (!groupName) {
        throw new Error('Invalid group path');
      }

      // Search for the group by name (returns parent groups with nested subGroups)
      const groups = await this.keycloakApi.getGroups(groupName);

      // Recursively search for the group with matching path in the nested structure
      const group = this.findGroupByPath(groups, groupPath);

      if (!group || !group.id) {
        throw new Error(`Group not found with path: ${groupPath}`);
      }

      return group.id;
    } catch (error) {
      console.error('Error resolving group ID:', error);
      return null;
    }
  }

  /**
   * Recursively search for a group by path in a nested group structure
   */
  private findGroupByPath(
    groups: GroupRepresentation[],
    targetPath: string
  ): GroupRepresentation | null {
    for (const group of groups) {
      // Check if this group matches the target path
      if (group.path === targetPath) {
        return group;
      }

      // Recursively search in subGroups if they exist
      if (group.subGroups && group.subGroups.length > 0) {
        const found = this.findGroupByPath(group.subGroups, targetPath);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  /**
   * Search users for autocomplete with debouncing
   */
  async searchUsers(event: Event): Promise<void> {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const searchCombobox = event?.target;

    const searchTerm = (event as CustomEvent)?.detail?.query || '';

    if (searchTerm.length < 2) {
      this.userSearchResults.set([]);
      return;
    }

    // Debounce search by 300ms
    this.searchTimeout = setTimeout(async () => {
      try {
        const users = await this.keycloakApi.searchUsersForGroupManagement(searchTerm);
        this.userSearchResults.set(users);
        setTimeout(() => {
          (searchCombobox as any)?.updateOptionsDisplay();
        }, 50);
      } catch (error) {
        console.error('Error searching users:', error);
        this.userSearchResults.set([]);
        setTimeout(() => {
          (searchCombobox as any)?.updateOptionsDisplay();
        }, 50);
      }
    }, 300);
  }

  /**
   * Handle user selection from combobox
   */
  onUserSelected(event: Event): void {
    const userId = (event as CustomEvent).detail?.value;
    this.selectedUserEmail.set(userId || null);
  }
  /**
   * Handle permission selection from combobox
   */
  onPermissionSelected(event: Event): void {
    const permission = (event as CustomEvent).detail?.value;
    this.selectedPermission.set(permission || null);
  }

  /**
   * Get display name for a user
   */
  getUserDisplayName(user: UserRepresentation): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username || user.email || user.id || 'Unknown';
  }

  /**
   * Get human-readable title for a permission relation name
   */
  getPermissionTitle(permission: string): string {
    return this.permissionDetails[permission]?.title ?? permission;
  }

  /**
   * Get human-readable description for a permission relation name
   */
  getPermissionDescription(permission: string): string {
    return this.permissionDetails[permission]?.description ?? '';
  }

  /**
   * Open add admin modal for a specific group type
   */
  openPermissionModal(): void {
    this.showPermissionModal.set(true);
    this.selectedUserEmail.set(null);
    this.selectedPermission.set(null);
    this.userSearchResults.set([]);
  }

  /**
   * Close add admin modal
   */
  closePermissionModal(): void {
    this.showPermissionModal.set(false);
    this.selectedUserEmail.set(null);
    this.selectedPermission.set(null);
    this.userSearchResults.set([]);
  }

  /**
   * Add a permission
   */
  addClientPermission(): void {
    if (!(this.selectedUserEmail() && this.selectedPermission())) {
      return;
    }
    if (
      this.clientPermissions.find(
        (perm) =>
          perm.user === this.selectedUserEmail() && perm.permission === this.selectedPermission()
      )
    ) {
      return; // already have it
    }
    this.clientPermissions = [
      ...this.clientPermissions,
      { user: this.selectedUserEmail()!, permission: this.selectedPermission()! },
    ];
  }

  /**
   * Remove a permission
   */
  removeClientPermission(toDelete: { user: string; permission: string }) {
    this.clientPermissions = this.clientPermissions.filter(
      (p) => !(p.user === toDelete.user && p.permission === toDelete.permission)
    );
  }

  /**
   * Serialize custom attribute filters array to JSON string
   */
  private serializeCustomAttributeFilters(
    filters: { attributeName: string; regex: string }[]
  ): string {
    const obj: Record<string, string> = {};
    filters.forEach((filter) => {
      if (filter.attributeName && filter.attributeName.trim() !== '') {
        obj[filter.attributeName.trim()] = filter.regex || '';
      }
    });
    return Object.keys(obj).length > 0 ? JSON.stringify(obj) : '';
  }

  // Localization management methods

  /**
   * Get a display name for a locale code (e.g. "en" -> "English")
   */
  getLocaleDisplayName(locale: string): string {
    try {
      const name = this.intlDisplayNames.of(locale);
      return name ? `${name} (${locale})` : locale;
    } catch {
      return locale;
    }
  }

  /**
   * Load available locales from the backend (cached in the service singleton)
   */
  async loadAvailableLocales(): Promise<void> {
    try {
      const locales = await this.keycloakApi.getAvailableLocales();
      this.availableLocales.set(locales);
    } catch (error) {
      console.error('Failed to load available locales:', error);
    }
  }

  /**
   * Load terms localization messages for this client
   */
  async loadTermsMessages(): Promise<void> {
    if (!this.clientId || !this.termsEnabled()) return;

    this.isLoadingTermsMessages.set(true);
    try {
      const result = await this.keycloakApi.getLocalizationMessages(
        this.client?.clientId ?? this.clientId!,
        'terms'
      );
      this.termsMessages.set(result.messages);
      this.originalTermsMessages.set({ ...result.messages });
      this.termsLocalizationKey.set(result.key);
    } catch (error) {
      console.error('Failed to load terms messages:', error);
    } finally {
      this.isLoadingTermsMessages.set(false);
    }
  }

  /**
   * Load deny message localization messages for this client
   */
  async loadDenyMessages(): Promise<void> {
    if (!this.clientId || !this.denyMessageEnabled()) return;

    this.isLoadingDenyMessages.set(true);
    try {
      const result = await this.keycloakApi.getLocalizationMessages(
        this.client?.clientId ?? this.clientId!,
        'deny.message'
      );
      this.denyMessages.set(result.messages);
      this.originalDenyMessages.set({ ...result.messages });
      this.denyLocalizationKey.set(result.key);
    } catch (error) {
      console.error('Failed to load deny messages:', error);
    } finally {
      this.isLoadingDenyMessages.set(false);
    }
  }

  /**
   * Enable terms localization for this client.
   * Sets the key and adds an empty English entry for editing.
   */
  enableTermsLocalization(): void {
    if (!this.client?.clientId) return;

    const key = `client.${this.client.clientId}.terms`;
    this.termsEnabled.set(true);
    this.termsLocalizationKey.set(key);
    this.clientForm.get('attributes.ebm_specific_terms_key')?.setValue(key);

    // Add empty English entry if not already present
    const current = this.termsMessages();
    if (!current['en']) {
      this.termsMessages.set({ ...current, en: '' });
    }
    // Mark originals as empty so all entries are treated as new/dirty
    this.originalTermsMessages.set({});
  }

  /**
   * Disable terms localization, deleting all translations from Keycloak
   * and clearing the client attribute.
   */
  async disableTermsLocalization(): Promise<void> {
    if (!this.clientId) return;

    this.isSavingLocalization.set(true);
    try {
      // Delete all locale entries and clear client attribute via backend
      await this.keycloakApi.deleteLocalizationMessage(
        this.client?.clientId ?? this.clientId,
        'terms'
      );

      this.termsEnabled.set(false);
      this.termsMessages.set({});
      this.originalTermsMessages.set({});
      this.termsLocalizationKey.set('');
      this.clientForm.get('attributes.ebm_specific_terms_key')?.setValue('');
      this.showDeleteTermsConfirm.set(false);
      this.successMessage = 'Terms localization removed successfully';
      setTimeout(() => (this.successMessage = null), 3000);
    } catch (error) {
      this.errorMessage = `Failed to remove terms localization: ${error}`;
      setTimeout(() => (this.errorMessage = null), 5000);
    } finally {
      this.isSavingLocalization.set(false);
    }
  }

  /**
   * Enable deny message localization for this client.
   */
  enableDenyMessageLocalization(): void {
    if (!this.client?.clientId) return;

    const key = `client.${this.client.clientId}.deny.message`;
    this.denyMessageEnabled.set(true);
    this.denyLocalizationKey.set(key);
    this.clientForm.get('denyAccess.messageKey')?.setValue(key);

    // Add empty English entry if not already present
    const current = this.denyMessages();
    if (!current['en']) {
      this.denyMessages.set({ ...current, en: '' });
    }
    // Mark originals as empty so all entries are treated as new/dirty
    this.originalDenyMessages.set({});
  }

  /**
   * Disable deny message localization, deleting all translations
   * and clearing the client attribute.
   */
  async disableDenyMessageLocalization(): Promise<void> {
    if (!this.clientId) return;

    this.isSavingLocalization.set(true);
    try {
      await this.keycloakApi.deleteLocalizationMessage(
        this.client?.clientId ?? this.clientId,
        'deny.message'
      );

      this.denyMessageEnabled.set(false);
      this.denyMessages.set({});
      this.originalDenyMessages.set({});
      this.denyLocalizationKey.set('');
      this.clientForm.get('denyAccess.messageKey')?.setValue('');
      this.showDeleteDenyConfirm.set(false);
      this.successMessage = 'Deny message localization removed successfully';
      setTimeout(() => (this.successMessage = null), 3000);
    } catch (error) {
      this.errorMessage = `Failed to remove deny message localization: ${error}`;
      setTimeout(() => (this.errorMessage = null), 5000);
    } finally {
      this.isSavingLocalization.set(false);
    }
  }

  /**
   * Save all permissions along with the attributes
   */
  private async saveAllPermissions(): Promise<void> {
    if (this.me()?.permissions?.includes('ia_full_admin')) {
      await this.keycloakApi.saveClientPermissions(this.client!.clientId!, this.clientPermissions);
    }
  }

  /**
   * Save all dirty localization messages (terms + deny) in one batch.
   * Handles creates, updates and deletes by diffing current vs. original state.
   * If all locales for a message type were removed, also clears the key via
   * a no-locale DELETE call (same as "Remove All").
   * Called as part of the main client save flow.
   */
  private async saveAllLocalizations(): Promise<void> {
    if (!this.clientId) return;

    // Use human-readable clientId so the lambda can skip UUID → name resolution
    const localizationClientId = this.client?.clientId ?? this.clientId;
    const promises: Promise<void>[] = [];

    // --- Terms ---
    const termsOriginal = this.originalTermsMessages();
    const termsCurrent = this.termsMessages();
    const termsOriginalHadEntries = Object.keys(termsOriginal).length > 0;
    const termsCurrentHasEntries = Object.keys(termsCurrent).length > 0;

    let savingTerms = false;
    if (
      (this.termsEnabled() || termsOriginalHadEntries) &&
      this.me()?.permissions?.includes('ia_terms')
    ) {
      savingTerms = true;
      // Upsert changed / new entries
      for (const [locale, text] of Object.entries(termsCurrent)) {
        if (termsOriginal[locale] !== text) {
          promises.push(
            this.keycloakApi.saveLocalizationMessage(localizationClientId, 'terms', locale, text)
          );
        }
      }

      // Delete removed entries
      for (const locale of Object.keys(termsOriginal)) {
        if (!(locale in termsCurrent)) {
          promises.push(
            this.keycloakApi.deleteLocalizationMessage(localizationClientId, 'terms', locale)
          );
        }
      }

      // If all locales were removed, also clear the key attribute via backend
      if (termsOriginalHadEntries && !termsCurrentHasEntries && !this.termsEnabled()) {
        promises.push(this.keycloakApi.deleteLocalizationMessage(localizationClientId, 'terms'));
      }
    }

    // --- Deny messages ---
    const denyOriginal = this.originalDenyMessages();
    const denyCurrent = this.denyMessages();
    const denyOriginalHadEntries = Object.keys(denyOriginal).length > 0;
    const denyCurrentHasEntries = Object.keys(denyCurrent).length > 0;

    let savingDeny = false;
    if (
      (this.denyMessageEnabled() || denyOriginalHadEntries) &&
      this.me()?.permissions?.includes('ia_denyconfig')
    ) {
      savingDeny = true;
      // Upsert changed / new entries
      for (const [locale, text] of Object.entries(denyCurrent)) {
        if (denyOriginal[locale] !== text) {
          promises.push(
            this.keycloakApi.saveLocalizationMessage(
              localizationClientId,
              'deny.message',
              locale,
              text
            )
          );
        }
      }

      // Delete removed entries
      for (const locale of Object.keys(denyOriginal)) {
        if (!(locale in denyCurrent)) {
          promises.push(
            this.keycloakApi.deleteLocalizationMessage(localizationClientId, 'deny.message', locale)
          );
        }
      }

      // If all locales were removed, also clear the key attribute via backend
      if (denyOriginalHadEntries && !denyCurrentHasEntries && !this.denyMessageEnabled()) {
        promises.push(
          this.keycloakApi.deleteLocalizationMessage(localizationClientId, 'deny.message')
        );
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
    // Update originals to match current after successful save
    if (savingTerms) {
      this.originalTermsMessages.set({ ...this.termsMessages() });
    }
    if (savingDeny) {
      this.originalDenyMessages.set({ ...this.denyMessages() });
    }
  }

  /**
   * Remove a locale from terms messages locally.
   * Actual API deletion happens on save. If no locales remain, auto-disables terms localization.
   */
  deleteTermsLocale(locale: string): void {
    const current = { ...this.termsMessages() };
    delete current[locale];
    this.termsMessages.set(current);

    // If no locales remain, auto-disable (same as "Remove All")
    if (Object.keys(current).length === 0) {
      this.termsEnabled.set(false);
      this.termsLocalizationKey.set('');
      this.clientForm.get('attributes.ebm_specific_terms_key')?.setValue('');
    }
  }

  /**
   * Remove a locale from deny messages locally.
   * Actual API deletion happens on save. If no locales remain, auto-disables deny message localization.
   */
  deleteDenyLocale(locale: string): void {
    const current = { ...this.denyMessages() };
    delete current[locale];
    this.denyMessages.set(current);

    // If no locales remain, auto-disable (same as "Remove All")
    if (Object.keys(current).length === 0) {
      this.denyMessageEnabled.set(false);
      this.denyLocalizationKey.set('');
      this.clientForm.get('denyAccess.messageKey')?.setValue('');
    }
  }

  /**
   * Add a new locale entry to terms messages for editing
   */
  addTermsLocale(localeSelect: HTMLElement): void {
    const select = localeSelect as HTMLSelectElement;
    const locale = select.value;
    if (!locale) return;
    const current = this.termsMessages();
    if (!current[locale]) {
      this.termsMessages.set({ ...current, [locale]: '' });
    }
    select.value = '';
  }

  /**
   * Add a new locale entry to deny messages for editing
   */
  addDenyLocale(localeSelect: HTMLElement): void {
    const select = localeSelect as HTMLSelectElement;
    const locale = select.value;
    if (!locale) return;
    const current = this.denyMessages();
    if (!current[locale]) {
      this.denyMessages.set({ ...current, [locale]: '' });
    }
    select.value = '';
  }

  /**
   * Update a locale's text in local state (called on textarea input)
   */
  updateTermsText(locale: string, text: string): void {
    this.termsMessages.set({ ...this.termsMessages(), [locale]: text });
  }

  /**
   * Update a locale's text in local state (called on textarea input)
   */
  updateDenyText(locale: string, text: string): void {
    this.denyMessages.set({ ...this.denyMessages(), [locale]: text });
  }
}
